import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatError } from "../lib/api";
import API from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Gem, Eye, EyeOff, Wallet, MailWarning, AlertTriangle, ShieldCheck } from "lucide-react";
import { useSiteSettings } from "../contexts/SiteSettingsContext";

export default function LoginPage() {
  const { login, loginWith2FA } = useAuth();
  const navigate = useNavigate();
  const { siteTitle } = useSiteSettings();
  const [tab, setTab] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fpAddress, setFpAddress] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [fpEnabled, setFpEnabled] = useState(false);
  const [verificationNeeded, setVerificationNeeded] = useState(false);
  const [wrongFormHint, setWrongFormHint] = useState(null); // {type: "faucetpay_only" | "email_exists", message}
  // 2FA state
  const [twofa, setTwofa] = useState(null); // { challenge_token, email }
  const [twofaCode, setTwofaCode] = useState("");

  useEffect(() => {
    API.get("/faucetpay/status").then(r => setFpEnabled(r.data.login_enabled)).catch(() => {});
  }, []);

  const handleEmail = async (e) => {
    e.preventDefault();
    setError("");
    setVerificationNeeded(false);
    setWrongFormHint(null);
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requires_2fa) {
        setTwofa({ challenge_token: result.challenge_token, email: result.email });
        return;
      }
      if (result?.email_verification_required) {
        setVerificationNeeded(true);
        return;
      }
      if (result?.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "";
      if (typeof detail === "string" && detail.startsWith("FAUCETPAY_ONLY:")) {
        setWrongFormHint({ type: "faucetpay_only", message: detail.replace("FAUCETPAY_ONLY:", "") });
      } else {
        setError(formatError(detail));
      }
    } finally { setLoading(false); }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await loginWith2FA(twofa.challenge_token, twofaCode.trim());
      setTwofa(null);
      setTwofaCode("");
      if (user?.role === "admin") navigate("/admin"); else navigate("/dashboard");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleFaucetPay = async (e) => {
    e.preventDefault();
    setError("");
    setWrongFormHint(null);
    setLoading(true);
    try {
      const { data } = await API.post("/auth/faucetpay", { address: fpAddress });
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
      window.location.href = "/dashboard";
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center">
              <Gem className="w-6 h-6 text-[#00F0FF]" />
            </div>
            <span className="text-2xl font-bold text-[#F8FAFC]">{siteTitle}</span>
          </Link>
          <h1 className="text-2xl font-bold text-[#F8FAFC] mb-2">Welcome Back</h1>
          <p className="text-sm text-[#94A3B8]">Choose your login method</p>
        </div>

        {/* Verification needed message */}
        {verificationNeeded && (
          <div className="p-4 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/30 mb-4" data-testid="verification-needed">
            <div className="flex items-start gap-3">
              <MailWarning className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-[#F59E0B] mb-1">Email Not Verified</p>
                <p className="text-xs text-[#94A3B8]">Please verify your email before logging in. Check your inbox for the verification link.</p>
                <Link to="/register" className="text-xs text-[#00F0FF] hover:underline mt-2 inline-block" data-testid="resend-link">Go to registration page to resend</Link>
              </div>
            </div>
          </div>
        )}

        {/* 2FA step */}
        {twofa ? (
          <form onSubmit={handle2FA} className="bg-[#131722] rounded-xl border border-[#00F0FF]/30 p-8 space-y-5" data-testid="twofa-form">
            <div className="text-center mb-2">
              <div className="w-14 h-14 mx-auto rounded-full bg-[#00F0FF]/10 flex items-center justify-center mb-3">
                <ShieldCheck className="w-7 h-7 text-[#00F0FF]" />
              </div>
              <h3 className="text-lg font-bold text-[#F8FAFC]">Two-Factor Authentication</h3>
              <p className="text-sm text-[#94A3B8] mt-1">Enter the 6-digit code from your authenticator app</p>
              <p className="text-xs text-[#64748B] mt-0.5">{twofa.email}</p>
            </div>
            {error && (
              <div className="p-3 rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/30 text-sm text-[#EF4444]" data-testid="twofa-error">{error}</div>
            )}
            <div>
              <Label className="text-[#94A3B8] text-sm mb-2 block">Authenticator code</Label>
              <Input value={twofaCode} onChange={(e) => setTwofaCode(e.target.value)} placeholder="123456 or XXXX-XXXX" required autoFocus
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] font-mono-game text-lg tracking-widest text-center"
                data-testid="twofa-code-input" />
              <p className="text-xs text-[#64748B] mt-2">Lost your device? Use one of your backup codes (format: XXXX-XXXX).</p>
            </div>
            <button type="submit" disabled={loading || !twofaCode.trim()} data-testid="twofa-submit-btn"
              className="w-full py-3 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90 transition-all disabled:opacity-50 shadow-[0_0_15px_rgba(0,240,255,0.4)]">
              {loading ? "Verifying..." : "Verify & Continue"}
            </button>
            <button type="button" onClick={() => { setTwofa(null); setTwofaCode(""); setError(""); }}
              className="w-full py-2 text-xs text-[#64748B] hover:text-[#94A3B8]" data-testid="twofa-cancel-btn">
              Cancel and use a different account
            </button>
          </form>
        ) : (
        <>
        {/* Login Method Tabs */}
        <div className="flex gap-1 bg-[#131722] rounded-lg p-1 border border-[#2A3241]/40 mb-6">
          <button onClick={() => setTab("email")} data-testid="tab-email"
            className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest rounded-md transition-all ${tab === "email" ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "text-[#64748B] hover:text-[#94A3B8]"}`}>
            Email Login
          </button>
          {fpEnabled && (
            <button onClick={() => setTab("faucetpay")} data-testid="tab-faucetpay"
              className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest rounded-md transition-all flex items-center justify-center gap-1.5 ${tab === "faucetpay" ? "bg-[#FFD600]/10 text-[#FFD600]" : "text-[#64748B] hover:text-[#94A3B8]"}`}>
              <Wallet className="w-3.5 h-3.5" /> FaucetPay
            </button>
          )}
        </div>

        {/* Wrong form hint */}
        {wrongFormHint && (
          <div className="p-4 rounded-lg bg-[#B026FF]/10 border border-[#B026FF]/30 mb-4" data-testid="wrong-form-hint">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#B026FF] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-[#B026FF] mb-1">Wrong Login Method</p>
                <p className="text-xs text-[#94A3B8]">{wrongFormHint.message}</p>
                {wrongFormHint.type === "faucetpay_only" && fpEnabled && (
                  <button onClick={() => { setTab("faucetpay"); setWrongFormHint(null); setError(""); }}
                    className="text-xs text-[#FFD600] hover:underline mt-2 inline-block" data-testid="switch-to-faucetpay-btn">
                    Switch to FaucetPay Login
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="p-3 rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/30 text-sm text-[#EF4444] mb-4" data-testid="login-error">
            {error}
          </div>
        )}

        {tab === "email" ? (
          <form onSubmit={handleEmail} className="bg-[#131722] rounded-xl border border-[#2A3241] p-8 space-y-5" data-testid="login-form">
            <div>
              <Label className="text-[#94A3B8] text-sm mb-2 block">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="player@example.com" required
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#00F0FF] focus:ring-[#00F0FF]/20"
                data-testid="login-email-input" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm mb-2 block">Password</Label>
              <div className="relative">
                <Input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" required
                  className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#00F0FF] pr-10"
                  data-testid="login-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#94A3B8]">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full py-3 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90 transition-all disabled:opacity-50 shadow-[0_0_15px_rgba(0,240,255,0.4)]">
              {loading ? "Authenticating..." : "Login"}
            </button>
            <p className="text-center text-sm text-[#94A3B8]">
              No account? <Link to="/register" className="text-[#00F0FF] hover:underline" data-testid="login-register-link">Register here</Link>
            </p>
            <p className="text-center text-xs text-[#64748B]">
              <Link to="/forgot-password" className="hover:text-[#94A3B8] hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
            </p>
          </form>
        ) : (
          <form onSubmit={handleFaucetPay} className="bg-[#131722] rounded-xl border border-[#FFD600]/20 p-8 space-y-5" data-testid="faucetpay-login-form">
            <div className="text-center mb-2">
              <div className="w-14 h-14 mx-auto rounded-full bg-[#FFD600]/10 flex items-center justify-center mb-3">
                <Wallet className="w-7 h-7 text-[#FFD600]" />
              </div>
              <p className="text-sm text-[#94A3B8]">Enter your FaucetPay address to login or auto-register</p>
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm mb-2 block">FaucetPay Crypto Address</Label>
              <Input value={fpAddress} onChange={(e) => setFpAddress(e.target.value)} placeholder="Your BTC, LTC, or other crypto address" required
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#FFD600]"
                data-testid="faucetpay-address-input" />
              <p className="text-xs text-[#64748B] mt-2">Must be an address linked to your FaucetPay account</p>
            </div>
            <button type="submit" disabled={loading} data-testid="faucetpay-login-btn"
              className="w-full py-3 text-sm font-bold uppercase tracking-widest bg-[#FFD600] text-[#0B0E14] rounded-lg hover:bg-[#FFD600]/90 transition-all disabled:opacity-50 shadow-[0_0_15px_rgba(255,214,0,0.4)]">
              {loading ? "Verifying..." : "Login with FaucetPay"}
            </button>
            <p className="text-center text-xs text-[#64748B]">
              New accounts are auto-created if address is verified on FaucetPay
            </p>
          </form>
        )}
        </>
        )}
      </div>
    </div>
  );
}
